import { useEffect, useMemo, useState } from "react";
import { api, formatTRY, formatNum, toNum } from "@/lib/apiClient";
import { groupByCategory, CategoryHeader, OwnerBadge, LockBadge, useCanEdit } from "@/lib/groupBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Apple } from "lucide-react";
import { toast } from "sonner";
import { keepScroll } from "@/lib/keepScroll";

const EMPTY = {
  name: "", category: "Genel", protein_per_100g: 0, carbs_per_100g: 0, fat_per_100g: 0, fiber_per_100g: 0, piece_grams: 0,
  calories_per_100g: 0, vp_per_100g: 0, unit: "g", cost_per_100g: 0, current_stock: 0, min_stock_alert: 0,
  package_grams: "", package_price: "", package_calories: "", package_vp: "",
};

const UNITS = [["g", "Gram (g)"], ["ml", "Mililitre (ml)"], ["adet", "Adet"]];
const baseOf = (unit) => (unit === "adet" ? 1 : 100);
const baseLabel = (unit) => (unit === "adet" ? "1 adet" : `100 ${unit || "g"}`);

const FIELDS = [
  { k: "name", l: "Malzeme Adı", type: "text" },
  { k: "category", l: "Kategori", type: "text" },
  { k: "protein_per_100g", l: "Protein", type: "number", base: true },
  { k: "carbs_per_100g", l: "Karbonhidrat", type: "number", base: true },
  { k: "fat_per_100g", l: "Yağ", type: "number", base: true },
  { k: "fiber_per_100g", l: "Lif", type: "number", base: true },
  { k: "piece_grams", l: "1 Adet / Ölçek Gramajı", type: "number", notAdet: true },
];

const STOCK_FIELDS = [
  { k: "current_stock", l: "Mevcut Stok" },
  { k: "min_stock_alert", l: "Kritik Stok Seviyesi" },
];

const PACKAGE_FIELDS = [
  { k: "package_grams", l: "Paket Miktarı", unitLabel: true },
  { k: "package_price", l: "Paket Fiyatı ₺" },
  { k: "package_calories", l: "Paket Toplam Kalori" },
  { k: "package_vp", l: "Paket Toplam VP" },
];

const perBase = (total, qty, unit) => (toNum(qty) > 0 ? (toNum(total) || 0) / toNum(qty) * baseOf(unit) : 0);

export default function Ingredients() {
  const canEdit = useCanEdit();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [stockMode, setStockMode] = useState("unit");
  const pg = toNum(form.package_grams) || 0;
  const toGrams = (v) => (stockMode === "package" && pg > 0 ? (toNum(v) || 0) * pg : (toNum(v) || 0));

  const load = () => keepScroll(() => api.get("/ingredients").then((r) => setItems(r.data)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setStockMode("unit"); setOpen(true); };
  const openEdit = (it) => {
    const f = { ...it };
    if (!(toNum(it.package_grams) > 0)) {
      f.package_grams = baseOf(it.unit); f.package_price = it.cost_per_100g; f.package_calories = it.calories_per_100g; f.package_vp = it.vp_per_100g;
    }
    setStockMode("unit");
    setForm(f); setEditing(it.id); setOpen(true);
  };

  const unit = form.unit || "g";
  const visibleFields = useMemo(() => FIELDS.filter((f) => !(f.notAdet && unit === "adet")), [unit]);
  const calc = {
    cost: perBase(form.package_price, form.package_grams, unit),
    kcal: perBase(form.package_calories, form.package_grams, unit),
    vp: perBase(form.package_vp, form.package_grams, unit),
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Malzeme adı gerekli");
    if (!(toNum(form.package_grams) > 0)) return toast.error("Paket miktarını girin");
    const payload = { ...form };
    FIELDS.forEach((f) => { if (f.type === "number") payload[f.k] = toNum(payload[f.k]) || 0; });
    PACKAGE_FIELDS.forEach((f) => { payload[f.k] = toNum(payload[f.k]) || 0; });
    STOCK_FIELDS.forEach((f) => { payload[f.k] = toGrams(payload[f.k]); });
    if (unit === "adet") payload.piece_grams = 0;
    payload.cost_per_100g = Math.round(calc.cost * 1000000) / 1000000;
    payload.calories_per_100g = Math.round(calc.kcal * 10000) / 10000;
    payload.vp_per_100g = Math.round(calc.vp * 1000000) / 1000000;
    try {
      if (editing) await api.put(`/ingredients/${editing}`, payload);
      else await api.post("/ingredients", payload);
      toast.success(editing ? "Malzeme güncellendi" : "Malzeme eklendi");
      keepScroll(async () => { setOpen(false); await load(); });
    } catch (e) {
      toast.error("İşlem başarısız");
    }
  };

  const del = async (id) => {
    await api.delete(`/ingredients/${id}`);
    toast.success("Malzeme silindi");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Malzeme Deposu</h1>
          <p className="mt-1 text-sm text-muted-foreground">Paket bilgisinden otomatik hesaplanan birim başına (100 g / 100 ml / 1 adet) besin değerleri, VP ve maliyet</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-ingredient-btn" onClick={openNew} className="font-semibold">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Malzeme
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Malzeme Düzenle" : "Yeni Malzeme"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
                <Label className="text-xs">Ölçü Birimi</Label>
                <div className="mt-2 flex gap-1 rounded-lg bg-secondary/60 p-1" data-testid="ingredient-unit">
                  {UNITS.map(([u, l]) => (
                    <button key={u} type="button" data-testid={`ingredient-unit-${u}`} onClick={() => setForm({ ...form, unit: u })}
                      className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${unit === u ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {unit === "adet" ? "Besin değerleri, maliyet ve stok 1 adet üzerinden; reçetelerde adet olarak kullanılır." : `Besin değerleri ve maliyet ${baseLabel(unit)} üzerinden; stok ve reçeteler ${unit} cinsinden.`}
                </p>
              </div>
              {visibleFields.map((f) => (
                <div key={f.k} className={f.k === "name" ? "col-span-2" : ""}>
                  <Label className="text-xs">{f.l}{f.base ? ` (${baseLabel(unit)})` : ""}</Label>
                  <Input
                    data-testid={`ingredient-${f.k}`}
                    type={f.type === "number" ? "text" : f.type}
                    inputMode={f.type === "number" ? "decimal" : undefined}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-border bg-background/50 p-3">
              <p className="text-xs font-semibold">Paket / Alış Bilgisi</p>
              <p className="mb-3 text-[11px] text-muted-foreground">Aldığınız paketin toplam miktarı, fiyatı, kalorisi ve VP'sini girin — {baseLabel(unit)} değerleri otomatik hesaplanır.</p>
              <div className="grid grid-cols-2 gap-3">
                {PACKAGE_FIELDS.map((f) => (
                  <div key={f.k}>
                    <Label className="text-xs">{f.l}{f.unitLabel ? ` (${form.unit || "g"})` : ""}</Label>
                    <Input data-testid={`ingredient-${f.k}`} type="text" inputMode="decimal" value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1" />
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="per100-preview">
                {[[`Maliyet / ${baseLabel(unit)}`, formatTRY(calc.cost)], [`Kalori / ${baseLabel(unit)}`, formatNum(calc.kcal, 0)], [`VP / ${baseLabel(unit)}`, formatNum(calc.vp, 2)]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 px-2 py-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p>
                    <p className="font-mono text-sm font-bold text-primary">{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background/50 p-3">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs font-semibold">Stok</p>
                <div className="flex gap-1 rounded-lg bg-secondary/60 p-1" data-testid="stock-mode">
                  {[["unit", form.unit || "g"], ["package", "Paket"]].map(([m, l]) => (
                    <button key={m} type="button" data-testid={`stock-mode-${m}`} disabled={m === "package" && !(pg > 0)} onClick={() => {
                      if (m === stockMode) return;
                      const conv = (v) => (m === "package" ? (toNum(v) || 0) / pg : (toNum(v) || 0) * pg);
                      setForm({ ...form, current_stock: Math.round(conv(form.current_stock) * 100) / 100, min_stock_alert: Math.round(conv(form.min_stock_alert) * 100) / 100 });
                      setStockMode(m);
                    }}
                      className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${stockMode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {STOCK_FIELDS.map((f) => (
                  <div key={f.k}>
                    <Label className="text-xs">{f.l} ({stockMode === "package" ? "paket" : form.unit || "g"})</Label>
                    <Input data-testid={`ingredient-${f.k}`} type="text" inputMode="decimal" value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-1" />
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`${f.k}-equiv`}>
                      {stockMode === "package" ? `= ${formatNum(toGrams(form[f.k]), 0)} ${form.unit || "g"}` : pg > 0 ? `≈ ${formatNum((toNum(form[f.k]) || 0) / pg, 2)} paket` : ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-ingredient-btn" onClick={save} className="font-semibold">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Malzeme</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Birim</TableHead>
                <TableHead className="text-right">Protein</TableHead>
                <TableHead className="text-right">Karb.</TableHead>
                <TableHead className="text-right">Yağ</TableHead>
                <TableHead className="text-right">Lif</TableHead>
                <TableHead className="text-right">Kalori</TableHead>
                <TableHead className="text-right">VP</TableHead>
                <TableHead className="text-right">Maliyet</TableHead>
                <TableHead className="text-right">Paket</TableHead>
                <TableHead className="text-right">Stok</TableHead>
                <TableHead className="text-right">Stok Değeri</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupByCategory(items).map((g) => [
                <TableRow key={`h-${g.category}`} className="hover:bg-transparent" data-testid={`category-header-${g.category}`}>
                  <TableCell colSpan={14} className="bg-secondary/30 py-2">
                    <span className="font-heading text-xs font-extrabold uppercase tracking-[0.2em] text-primary">{g.category}</span>
                    <span className="ml-2 text-[11px] text-muted-foreground">{g.items.length} malzeme</span>
                  </TableCell>
                </TableRow>,
                ...g.items.map((it) => (
                <TableRow key={it.id} data-testid={`ingredient-row-${it.id}`}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell className="text-muted-foreground">{it.category} <OwnerBadge ownerId={it.owner_id} /> <LockBadge item={it} /></TableCell>
                  <TableCell><span className="rounded-full bg-secondary px-2 py-0.5 font-mono text-[11px]" data-testid={`ingredient-unit-badge-${it.id}`}>{baseLabel(it.unit)}</span></TableCell>
                  <TableCell className="text-right font-mono">{formatNum(it.protein_per_100g)}g</TableCell>
                  <TableCell className="text-right font-mono">{formatNum(it.carbs_per_100g)}g</TableCell>
                  <TableCell className="text-right font-mono">{formatNum(it.fat_per_100g)}g</TableCell>
                  <TableCell className="text-right font-mono">{formatNum(it.fiber_per_100g)}g</TableCell>
                  <TableCell className="text-right font-mono">{formatNum(it.calories_per_100g, 0)}</TableCell>
                  <TableCell className="text-right">
                    <span className="rounded-full bg-chart-5/15 px-2 py-0.5 font-mono text-xs" style={{ color: "var(--chart-5)" }}>
                      {formatNum(it.vp_per_100g, 2)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatTRY(it.cost_per_100g)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                    {it.package_grams > 0 ? `${formatNum(it.package_grams, 0)} ${it.unit} · ${formatTRY(it.package_price)}` : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatNum(it.current_stock, 0)} {it.unit}
                    {it.package_grams > 0 && <span className="block text-[11px] text-muted-foreground">≈ {formatNum(it.current_stock / it.package_grams, 1)} paket</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatTRY((it.current_stock || 0) / baseOf(it.unit) * (it.cost_per_100g || 0))}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" data-testid={`edit-ingredient-${it.id}`} disabled={!canEdit(it)} onClick={() => openEdit(it)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-ingredient-${it.id}`} disabled={!canEdit(it)} onClick={() => del(it.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                )),
              ])}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} className="py-12 text-center text-muted-foreground">
                    <Apple className="mx-auto mb-2 h-8 w-8 opacity-40" />
                    Henüz malzeme eklenmedi
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
